import { ChangeDetectionStrategy, Component, Injector, OnInit, forwardRef, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALIDATORS, NgControl, Validator } from '@angular/forms';
import { AsYouType, CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js/min';

import { TEL_COUNTRIES } from './tel-countries';

let nextId = 0;

/** Country-code + national-number control emitting E.164 (`+9715XXXXXXXX`) via
 *  libphonenumber-js. Same valueAccessor pattern as TextInputComponent, plus
 *  NG_VALIDATORS for built-in "invalidPhone" format validation. */
@Component({
    selector: 'app-tel-input',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './tel-input.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => TelInputComponent), multi: true }],
})
export class TelInputComponent implements ControlValueAccessor, Validator, OnInit {
    label = input.required<string>();
    placeholder = input('50 123 4567');
    defaultCountry = input<CountryCode>('AE');
    inputRequiredIndicatorEnabled = input<boolean>(false);

    readonly inputId = `tel-input-${nextId++}`;
    readonly countries = TEL_COUNTRIES;

    readonly country = signal<CountryCode>('AE');
    readonly nationalNumber = signal('');

    ngControl!: NgControl;
    private readonly _injector = inject(Injector);

    constructor() {
        this.country.set(this.defaultCountry());
    }

    ngOnInit(): void {
        this.ngControl = this._injector.get(NgControl, null, { self: true })!;
        this.ngControl.valueAccessor = this;
    }

    writeValue(value: string | null): void {
        if (!value) {
            this.nationalNumber.set('');
            return;
        }
        const parsed = parsePhoneNumberFromString(value);
        if (parsed) {
            this.country.set(parsed.country ?? this.country());
            this.nationalNumber.set(parsed.formatNational());
        } else {
            this.nationalNumber.set(value);
        }
    }

    registerOnChange(): void {}
    registerOnTouched(): void {}

    validate() {
        const raw = this.nationalNumber().trim();
        if (!raw) return null;
        const parsed = parsePhoneNumberFromString(raw, this.country());
        return parsed?.isValid() ? null : { invalidPhone: true };
    }

    onCountryChange(iso: string): void {
        this.country.set(iso as CountryCode);
        this._emit();
    }

    onNumberInput(value: string): void {
        const formatted = new AsYouType(this.country()).input(value);
        this.nationalNumber.set(formatted);
        this._emit();
    }

    onBlur(): void {
        // AsYouType skips separators for some countries (e.g. UAE) — reformat on blur.
        const raw = this.nationalNumber().trim();
        if (raw) {
            const parsed = parsePhoneNumberFromString(raw, this.country());
            if (parsed?.isValid()) this.nationalNumber.set(parsed.formatNational());
        }
        this.ngControl.control?.markAsTouched();
    }

    private _emit(): void {
        const raw = this.nationalNumber().trim();
        if (!raw) {
            this.ngControl.control?.setValue(null, { emitModelToViewChange: false });
            return;
        }
        const parsed = parsePhoneNumberFromString(raw, this.country());
        this.ngControl.control?.setValue(parsed?.isValid() ? parsed.number : raw, { emitModelToViewChange: false });
    }
}
